import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Handshake } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, Field, Input, Select, SubmitButton } from "@/components/shared";
import { submitLead, formatApiError } from "@/lib/api";

export default function HostPartnerForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", property_location: "", bedrooms: "", listing_url: "", monthly_turnovers: "", preferred_start: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead("host_partner", form);
      setDone(true);
      toast.success("Request received — welcome aboard soon.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="host-partner" className="scroll-mt-24 bg-navy text-ivory" data-testid="host-partner-section">
      <div className="noise-overlay mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-28">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-24">
          <div>
            <SectionHead
              dark
              overline="For Airbnb & VRBO Hosts"
              title={<>Lock in your<br /><span className="italic text-bermuda">Host Partner Rate.</span></>}
              copy="Guarantee us regular turnovers and your rate drops — a 2-bedroom becomes $85 every time, locked in. You get a reliable team that treats your reviews like their own; we get the recurring work we'd rather have than one-off premiums."
            />
            <div className="mt-10 space-y-5">
              {[
                "Partner rates: 1BR $65 · 2BR $85 · 3BR $105 · 4BR $130 — every turnover, every time",
                "Same-day turnovers never carry a rush fee for partner hosts",
                "Every clean verified against the turnover checklist before your next guest arrives",
              ].map((text, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="flex items-start gap-4">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-bermuda/40 text-bermuda">
                      <Handshake size={15} strokeWidth={1.5} />
                    </span>
                    <p className="text-sm leading-relaxed text-ivory/75">{text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.15}>
            <div className="border border-white/10 bg-white/5 p-8 backdrop-blur-sm lg:p-10">
              {done ? (
                <div className="py-10 text-center" data-testid="host-partner-success">
                  <CheckCircle2 size={40} strokeWidth={1.2} className="mx-auto text-bermuda" />
                  <h3 className="mt-6 font-serif text-3xl">Welcome aboard — almost.</h3>
                  <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-ivory/65">
                    Your partner request is in. We'll confirm your rate and first turnover date personally, very shortly.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-serif text-2xl">Request the Partner Rate</h3>
                  <p className="mt-2 text-sm text-ivory/55">Two minutes now, cheaper turnovers forever.</p>
                  <form onSubmit={onSubmit} className="dark-form mt-8 grid gap-5 sm:grid-cols-2" data-testid="host-partner-form">
                    <Field label="Your Name" required><Input data-testid="hp-name" required value={form.name} onChange={set("name")} className="!border-white/20 !bg-navy !text-ivory placeholder:!text-ivory/35" /></Field>
                    <Field label="Email" required><Input data-testid="hp-email" required type="email" value={form.email} onChange={set("email")} className="!border-white/20 !bg-navy !text-ivory placeholder:!text-ivory/35" /></Field>
                    <Field label="Phone / WhatsApp" required><Input data-testid="hp-phone" required type="tel" value={form.phone} onChange={set("phone")} className="!border-white/20 !bg-navy !text-ivory placeholder:!text-ivory/35" /></Field>
                    <Field label="Property Location" required><Input data-testid="hp-location" required value={form.property_location} onChange={set("property_location")} placeholder="e.g. Warwick" className="!border-white/20 !bg-navy !text-ivory placeholder:!text-ivory/35" /></Field>
                    <Field label="Bedrooms" required>
                      <Select data-testid="hp-bedrooms" required value={form.bedrooms} onChange={set("bedrooms")} className="!border-white/20 !bg-navy !text-ivory">
                        <option value="">Select…</option>
                        {["Studio", "1", "2", "3", "4", "5+"].map((b) => <option key={b}>{b}</option>)}
                      </Select>
                    </Field>
                    <Field label="Expected Monthly Turnovers" required>
                      <Select data-testid="hp-turnovers" required value={form.monthly_turnovers} onChange={set("monthly_turnovers")} className="!border-white/20 !bg-navy !text-ivory">
                        <option value="">Select…</option>
                        {["1–3", "4–8", "9–15", "15+"].map((t) => <option key={t}>{t}</option>)}
                      </Select>
                    </Field>
                    <div className="sm:col-span-2"><Field label="Listing URL"><Input data-testid="hp-listing" value={form.listing_url} onChange={set("listing_url")} placeholder="https://airbnb.com/…" className="!border-white/20 !bg-navy !text-ivory placeholder:!text-ivory/35" /></Field></div>
                    <Field label="Preferred Start Date"><Input data-testid="hp-start" type="date" value={form.preferred_start} onChange={set("preferred_start")} className="!border-white/20 !bg-navy !text-ivory" /></Field>
                    <Field label="Notes"><Input data-testid="hp-notes" value={form.notes} onChange={set("notes")} placeholder="Access, laundry, quirks…" className="!border-white/20 !bg-navy !text-ivory placeholder:!text-ivory/35" /></Field>
                    <div className="sm:col-span-2">
                      <SubmitButton loading={loading} testid="host-partner-submit">Request My Partner Rate</SubmitButton>
                    </div>
                  </form>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
